import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  useDisclosure,
  VStack,
  Text,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { uploadCustomerIndicators } from "../../api/customers";
import { GenericModal } from "../generic/modal";
import { Context } from "../../ContextWrapper";
import { Select } from "chakra-react-select";
import axios from "axios";

export const GeneralToolsTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState([]);
  const context = React.useContext(Context);

  const {
    isOpen: isUploadOpen,
    onOpen: onUploadOpen,
    onClose: onUploadClose,
  } = useDisclosure();

  const {
    isOpen: isErrorOpen,
    onOpen: onErrorOpen,
    onClose: onErrorClose,
  } = useDisclosure();

  const handleUpload = (formData) => {
  setLoading(true);
  uploadCustomerIndicators(formData)
    .then((response) => {
      if (response.status === 200) {
        onUploadClose();
        setLoading(false);
        context.fireToast(
          "Success",
          "Successfully imported account data",
          "success"
        );
      } else {
        onUploadClose();
        setLoading(false);
        setError(response.response.data.errors);
        onErrorOpen();
      }
    })
    .catch((error) => {
      onUploadClose();
      setLoading(false);
      setError(error.response.data.errors);
      onErrorOpen();
      console.error("There was an error uploading the file!", error);
    });
};

  return (
    <Box p={4}>
      <Heading size="md" mb={4}>
        General Tools
      </Heading>
      <UploadModal
        isOpen={isUploadOpen}
        onClose={onUploadClose}
        loading={loading}
        handleUpload={handleUpload}
      />
      <ErrorModal isOpen={isErrorOpen} onClose={onErrorClose} errors={error} />
      <VStack spacing={3} align="stretch">
        <Button
          colorScheme="blue"
          justifyContent="flex-start"
          variant="outline"
          onClick={onUploadOpen}
        >
          Upload Monthly Targets
        </Button>
         <CustomerImportTools
          onSuccess={(msg) =>
            context.fireToast("Success", msg, "success")
          }
        />
      </VStack>
    </Box>
  );
};

export default function CustomerImportTools({ onSuccess }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file_uploaded", file);

    try {
      setLoading(true);
      setErrors(null);

      await axios.post("/api/v2/customer_data_import/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFile(null);
      onClose();
      onSuccess?.("Successfully imported year targets");
    } catch (err) {
      setErrors(
        err.response?.data?.errors || [
          { error: "Upload failed", detail: ["Server error"] },
        ]
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <VStack align="stretch">
        <Button
          variant="outline"
          colorScheme="blue"
          justifyContent="flex-start"
          onClick={onOpen}
        >
          Upload Year Targets 2026
        </Button>
      </VStack>

      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        title="Upload Customer Data"
        submitButton={
          <Button ml={5} onClick={handleUpload} isDisabled={!file || loading}>
            {loading ? "Processing..." : "Upload"}
          </Button>
        }
        body={
          <VStack spacing={5} align="stretch" mt={2}>
            <FormControl isRequired>
              <FormLabel>Excel File</FormLabel>
              <Input
                type="file"
                accept=".xlsx"
                onChange={(e) => setFile(e.target.files[0])}
                sx={{
                  "::file-selector-button": {
                    fontWeight: "600",
                    bg: "gray.100",
                    _hover: { bg: "gray.200" },
                    border: "1px solid",
                    borderColor: "gray.300",
                    borderRadius: "6px",
                    px: 3,
                    py: 1,
                  },
                }}
              />
            </FormControl>

            {errors &&
              errors.map((e, i) => (
                <Box key={i}>
                  <Text fontWeight="bold">{e.error}</Text>
                  {e.detail?.map((d, j) => (
                    <Text key={j}>{d}</Text>
                  ))}
                </Box>
              ))}
          </VStack>
        }
      />
    </Box>
  );
}

const UploadModal = ({ isOpen, onClose, loading, handleUpload }) => {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(null); // will hold option object
  const [year, setYear] = useState(null); // will hold option object

  const onUploadFile = (e) => {
    setFile(e.target.files[0]);
  };

  const months = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
  ];

  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const y = new Date().getFullYear() - 4 + i;
    return { label: `${y}`, value: `${y}` };
  });

  const monthOptions = months.map((m) => ({ label: m.label, value: m.value }));

  const submitUpload = () => {
    const formData = new FormData();
    formData.append("file_uploaded", file);
    // send month & year as strings like "2025-10-01" or separately as month/year
    formData.append("month", month?.value);
    formData.append("year", year?.value);

    handleUpload(formData);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Monthly Targets"
      submitButton={
        <Button
          ml={5}
          onClick={submitUpload}
          isDisabled={loading || !file || !month || !year}
        >
          {loading ? "Processing..." : "Upload"}
        </Button>
      }
      body={
        <VStack spacing={5} align="stretch" mt={2}>
          {/* File Upload */}
          <FormControl isRequired>
            <FormLabel>File</FormLabel>
            <Input
              type="file"
              accept=".xlsx"
              onChange={onUploadFile}
              sx={{
                "::file-selector-button": {
                  fontWeight: "600",
                  bg: "gray.100",
                  _hover: { bg: "gray.200" },
                  border: "1px solid",
                  borderColor: "gray.300",
                  borderRadius: "6px",
                  px: 3,
                  py: 1,
                },
              }}
            />
          </FormControl>

          {/* Month (chakra-react-select) */}
          <FormControl isRequired>
            <FormLabel>Month</FormLabel>
            <Select
              options={monthOptions}
              value={month}
              onChange={(opt) => setMonth(opt)}
              placeholder="Select month"
              chakraStyles={{
                dropdownIndicator: (provided) => ({ ...provided, px: 2 }),
              }}
            />
          </FormControl>

          {/* Year (chakra-react-select) */}
          <FormControl isRequired>
            <FormLabel>Year</FormLabel>
            <Select
              options={yearOptions}
              value={year}
              onChange={(opt) => setYear(opt)}
              placeholder="Select year"
            />
          </FormControl>
        </VStack>
      }
    />
  );
};

const ErrorModal = ({ isOpen, onClose, errors }) => {
  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Upload Errors`}
      body={
        <>
          {errors &&
            errors.map((e, i) => (
              <Box key={i}>
                <Text as="b">{e.error}</Text>
                {e.detail.map((d, i2) => (
                  <Text key={i + i2} mb={0}>
                    {d}
                  </Text>
                ))}
              </Box>
            ))}
          {!errors && <Text>Server error, please check template format</Text>}
        </>
      }
    ></GenericModal>
  );
};
